import { api, formatApiError } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Building2, Users, Trophy, Link2, Beer, BarChart3, Calendar, Upload, ShieldCheck } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LocalSponsorsCard } from "@/components/client/LocalSponsorsCard";

const SECTION = "bg-white rounded-sm border border-zinc-200";
const API_BASE = process.env.REACT_APP_BACKEND_URL;
const absoluteLogo = (u) => (u ? (u.startsWith("http") ? u : `${API_BASE}${u}`) : null);

// -----------------------------------------------------------------------
// Brewery-owner logo uploader (POST /clients/me/logo)
// -----------------------------------------------------------------------
function ClientLogoSelfUploader({ hasLogo, scope, onSaved }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2_000_000) {
      toast.error("Logo must be under 2 MB.");
      return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(scope("/clients/me/logo"), fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-2">
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="client-self-logo-input" />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        data-testid="client-self-logo-upload"
        className="inline-flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-3 h-8 disabled:opacity-50"
      >
        <Upload className="h-3.5 w-3.5" /> {busy ? "Uploading…" : hasLogo ? "Replace logo" : "Upload logo"}
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------
// Pub prize / branding inline editor
// -----------------------------------------------------------------------
function PubEditor({ pub, scope, onSaved }) {
  const [prizeText, setPrizeText] = useState(pub.prize_pool_text || "");
  const [prizePounds, setPrizePounds] = useState(((pub.prize_fund_pence || 0) / 100).toFixed(2));
  const [whatsappUrl, setWhatsappUrl] = useState(pub.whatsapp_group_invite_url || "");
  const [whatsappBody, setWhatsappBody] = useState(pub.whatsapp_welcome_message || "");
  const [whatsappInvite, setWhatsappInvite] = useState(pub.whatsapp_invite_text || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const trimmed = (whatsappUrl || "").trim();
      const trimmedBody = (whatsappBody || "").trim();
      const trimmedInvite = (whatsappInvite || "").trim();
      const payload = {
        prize_pool_text: prizeText,
        prize_fund_pence: Math.max(0, Math.round(parseFloat(prizePounds || "0") * 100)),
        // Empty string clears the field so the global fallback resumes.
        whatsapp_group_invite_url: trimmed,
        whatsapp_welcome_message: trimmedBody,
        whatsapp_invite_text: trimmedInvite,
      };
      const { data } = await api.put(scope(`/clients/me/pubs/${pub.id}`), payload);
      toast.success(`${data.name} saved`);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const landingUrl = `${window.location.origin}/${pub.slug}`;
  const primary = pub.primary_color || "#0038FF";
  // The pub's logo may be stored either as an Object-Store URL (`/api/logos/…`
  // returned as `logo_url`) or as an inline base64 data URI (`logo_data_url`,
  // legacy). Always prefer `logo_url` — fall back to the data URI. Prefixing
  // with the backend base ensures the deeper `/c/[slug]/editor` route can't
  // resolve the path relative to itself (`/c/…/api/logos/…` — broken).
  const pubLogoSrc = pub.logo_url
    ? (pub.logo_url.startsWith("http") ? pub.logo_url : `${API_BASE}${pub.logo_url}`)
    : (pub.logo_data_url || null);

  return (
    <div className={`${SECTION} p-5`} data-testid={`pub-editor-${pub.slug}`}>
      <div className="flex items-start gap-3">
        {pubLogoSrc ? (
          <img src={pubLogoSrc} alt="" className="h-12 w-12 rounded-sm object-contain border border-zinc-200 bg-white p-0.5" />
        ) : (
          <div className="h-12 w-12 rounded-sm flex items-center justify-center text-white font-bold" style={{ backgroundColor: primary }}>{pub.name.slice(0, 1)}</div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-heading text-lg font-bold text-zinc-950">{pub.name}</p>
          <a href={landingUrl} target="_blank" rel="noreferrer" className="text-[11px] font-mono text-zinc-500 hover:text-zinc-950 underline underline-offset-2 truncate block">
            /{pub.slug}
          </a>
          <p className="text-xs text-zinc-500 mt-1"><Users className="h-3 w-3 inline mr-1" />{pub.participants_count ?? 0} participants</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Season Prize description</Label>
          <Input value={prizeText} onChange={(e) => setPrizeText(e.target.value)} placeholder="e.g. £500 Bar Tab" data-testid={`pub-prize-text-${pub.slug}`} className="mt-2 h-10 rounded-sm border-zinc-300 text-sm" maxLength={280} />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">Season Prize (£)</Label>
          <div className="relative mt-2">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">£</span>
            <Input type="number" min={0} step="1" value={prizePounds} onChange={(e) => setPrizePounds(e.target.value)} data-testid={`pub-prize-fund-${pub.slug}`} className="h-10 pl-7 rounded-sm border-zinc-300 text-sm" />
          </div>
        </div>
      </div>
      <p className="text-[11px] text-zinc-500 mt-2">This is the amount awarded to your pub&#39;s Season Champion at close — overrides the platform-wide default.</p>

      <div className="mt-4 pt-4 border-t border-zinc-200">
        <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
          WhatsApp banter group invite URL
        </Label>
        <Input
          type="url"
          value={whatsappUrl}
          onChange={(e) => setWhatsappUrl(e.target.value)}
          placeholder="https://chat.whatsapp.com/…"
          data-testid={`pub-whatsapp-url-${pub.slug}`}
          autoComplete="off"
          spellCheck={false}
          className="mt-2 h-10 rounded-sm border-zinc-300 text-sm font-mono"
          maxLength={500}
        />
        <p className="text-[11px] text-zinc-500 mt-1">
          Sent by email to any user who ticks &quot;Add me to the WhatsApp
          group&quot; when signing up on{" "}
          <span className="font-mono">/{pub.slug}</span>. Leave blank to
          skip the WhatsApp invite for this pub.
        </p>

        <div className="mt-4">
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
            Welcome email body copy
          </Label>
          <Textarea
            value={whatsappBody}
            onChange={(e) => setWhatsappBody(e.target.value)}
            placeholder={"Thanks for opting into the WhatsApp banter group at {pub_name}. One tap below joins you straight away — bring the chat."}
            data-testid={`pub-whatsapp-body-${pub.slug}`}
            rows={4}
            maxLength={1200}
            className="mt-2 rounded-sm border-zinc-300 text-sm"
          />
          <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
            Use{" "}
            <span className="font-mono bg-zinc-100 px-1 rounded">
              {"{name}"}
            </span>{" "}
            for the recipient&#39;s first name and{" "}
            <span className="font-mono bg-zinc-100 px-1 rounded">
              {"{pub_name}"}
            </span>{" "}
            for your pub. A branded link to{" "}
            <span className="font-mono">
              play.amblemead.biz/{pub.slug}
            </span>{" "}
            is added automatically. Leave blank for the default copy.
          </p>
        </div>

        <div className="mt-4">
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
            &quot;Invite a friend&quot; WhatsApp text
          </Label>
          <Textarea
            value={whatsappInvite}
            onChange={(e) => setWhatsappInvite(e.target.value)}
            placeholder={"{user} invited you to play {pub_name} Football Challenge \u26BD Just predict 6 match stats over the 6 game league and see if you can beat me and win a prize. Sign up at {link}"}
            data-testid={`pub-whatsapp-invite-text-${pub.slug}`}
            rows={4}
            maxLength={1200}
            className="mt-2 rounded-sm border-zinc-300 text-sm"
          />
          <p className="text-[11px] text-zinc-500 mt-1 leading-relaxed">
            The pre-filled WhatsApp text every player sees when they
            tap the &quot;Invite a friend · WhatsApp&quot; button on
            your matches page. Available tokens:{" "}
            <span className="font-mono bg-zinc-100 px-1 rounded">{"{user}"}</span>{" "}
            (the sender&#39;s name),{" "}
            <span className="font-mono bg-zinc-100 px-1 rounded">
              {"{pub_name}"}
            </span>{" "}
            (this pub) and{" "}
            <span className="font-mono bg-zinc-100 px-1 rounded">{"{link}"}</span>{" "}
            (a personalised sign-up link back to /{pub.slug}). Leave
            blank to use the platform default.
          </p>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid={`pub-save-${pub.slug}`} className="h-9 rounded-sm text-white font-semibold text-xs px-4" style={{ backgroundColor: primary }}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Page
// -----------------------------------------------------------------------
export default function ClientDashboardPage() {
  const { slug } = useParams();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [pubs, setPubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // iter88 — /c/[slug]/editor impersonation. Both Super-Admins AND Editors
  // (users with `is_editor=true`) append `?as_client=<slug>` to every
  // /clients/me/* call so the backend scopes to that pub's parent client
  // via `_require_client`. Client-role users pass through unchanged.
  const isImpersonating =
    (user?.role === "admin" || !!user?.is_editor) && !!slug;
  const scope = useMemo(() => {
    return (path) => {
      if (!isImpersonating) return path;
      const sep = path.includes("?") ? "&" : "?";
      return `${path}${sep}as_client=${encodeURIComponent(slug)}`;
    };
  }, [isImpersonating, slug]);

  const load = useCallback(async () => {
    try {
      const [c, p, s] = await Promise.all([
        api.get(scope("/clients/me")),
        api.get(scope("/clients/me/pubs")),
        api.get(scope("/clients/me/stats")),
      ]);
      setClient(c.data);
      setPubs(p.data);
      setStats(s.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !client) {
    return (
      <div className="min-h-screen bg-zinc-50">
        <Header />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-sm text-zinc-500">Loading dashboard…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="client-dashboard">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24" data-testid="client-editor-dashboard">
        {isImpersonating && (
          <div
            data-testid="impersonation-banner"
            className="mb-6 flex items-center gap-3 px-4 py-3 rounded-sm border border-amber-400 bg-amber-50 text-amber-900"
          >
            <ShieldCheck className="h-4 w-4 shrink-0" />
            <p className="text-xs font-semibold">
              Super-Admin view — managing <span className="font-mono">{slug}</span> on behalf of the client. Every change is written to their record.
            </p>
          </div>
        )}

        {/* Big title block — mirrors the master /editor page. */}
        <div className="mb-10 flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-2">
              <ShieldCheck className="h-3.5 w-3.5" /> Client editor
            </p>
            <h1
              data-testid="client-dashboard-name"
              className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950 mt-1"
            >
              {client.name} Editor.
            </h1>
            <p className="text-sm text-zinc-500 mt-3 max-w-2xl">
              Pick this week&apos;s matches, set your house prize, and add local sponsor tiles that appear on your branded landing page. All changes save immediately.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {client.logo_url ? (
              <img
                src={absoluteLogo(client.logo_url)}
                alt={`${client.name} logo`}
                data-testid="client-dashboard-logo"
                className="h-16 w-16 rounded-sm object-contain bg-white border border-zinc-200 p-0.5"
              />
            ) : (
              <div className="h-16 w-16 rounded-sm bg-zinc-950 text-white flex items-center justify-center" data-testid="client-dashboard-logo-fallback">
                <Building2 className="h-7 w-7" />
              </div>
            )}
            <ClientLogoSelfUploader hasLogo={!!client.logo_url} scope={scope} onSaved={load} />
          </div>
        </div>

        {/* Aggregate stats — 4-card grid matches the editor's stats style. */}
        <section className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="client-stats-cards">
          {[
            { label: "Pubs", value: stats?.pubs_count ?? 0, icon: Beer, tid: "stat-pubs" },
            { label: "Participants", value: stats?.participants_count ?? 0, icon: Users, tid: "stat-participants" },
            { label: "Predictions", value: stats?.predictions_count ?? 0, icon: BarChart3, tid: "stat-predictions" },
            { label: "Active matches", value: stats?.selected_matches_count ?? 0, icon: Calendar, tid: "stat-matches" },
          ].map((s) => (
            <div key={s.label} className={`${SECTION} p-4`} data-testid={s.tid}>
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500"><s.icon className="h-3.5 w-3.5" /> {s.label}</div>
              <p className="mt-2 font-heading text-3xl font-bold text-zinc-950">{s.value}</p>
            </div>
          ))}
        </section>

        <div className="mt-12 grid grid-cols-1 gap-6">
          <section className={`${SECTION} p-6`} data-testid="pubs-panel">
            <div className="flex items-center gap-2 mb-5">
              <Beer className="h-4 w-4 text-zinc-500" />
              <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950">Your pubs ({pubs.length})</h2>
            </div>
            {pubs.length === 0 ? (
              <p className="text-sm text-zinc-500" data-testid="pubs-empty">
                No pubs yet. Ask the admin to add them under your client, or add them yourself if you have super-admin.
              </p>
            ) : (
              <div className="space-y-3">
                {pubs.map((p) => (
                  <PubEditor key={p.id} pub={p} scope={scope} onSaved={load} />
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Per-pub local sponsors — one card per pub, editable by the client
            owner + super-admin. Replaces global Dugsy sponsors on /[slug]. */}
        {pubs.length > 0 && (
          <section className="mt-12 space-y-4" data-testid="local-sponsors-panel">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="h-4 w-4 text-zinc-500" />
              <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950">Local sponsors</h2>
            </div>
            <p className="text-sm text-zinc-500 max-w-2xl">
              Sponsor tiles configured here replace the master sponsorship panel on this pub&apos;s branded landing page. Leave empty to show just your logo watermark.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {pubs.map((p) => (
                <LocalSponsorsCard key={p.id} pub={p} scope={scope} onSaved={load} />
              ))}
            </div>
          </section>
        )}

        {/* Per-pub aggregate breakdown */}
        {stats && stats.per_pub && stats.per_pub.length > 0 && (
          <section className={`mt-12 ${SECTION} p-6`} data-testid="per-pub-breakdown">
            <div className="flex items-center gap-2 mb-5">
              <Trophy className="h-4 w-4 text-zinc-500" />
              <h2 className="font-heading text-xl font-bold tracking-tight text-zinc-950">Pub performance</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-200">
                    <th className="py-2 pr-3">Pub</th>
                    <th className="py-2 pr-3 text-right">Participants</th>
                    <th className="py-2 pr-3 text-right">Predictions</th>
                    <th className="py-2 pr-3 text-right">Total points</th>
                    <th className="py-2 pr-3">Landing link</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.per_pub.map((r) => (
                    <tr key={r.pub_id} className="border-b border-zinc-100" data-testid={`per-pub-row-${r.slug}`}>
                      <td className="py-2 pr-3 font-semibold text-zinc-950">{r.name}</td>
                      <td className="py-2 pr-3 text-right">{r.participants}</td>
                      <td className="py-2 pr-3 text-right">{r.predictions}</td>
                      <td className="py-2 pr-3 text-right font-mono">{r.total_points}</td>
                      <td className="py-2 pr-3">
                        <a href={`/${r.slug}`} target="_blank" rel="noreferrer" className="text-xs font-semibold text-zinc-600 hover:text-zinc-950 underline underline-offset-2 inline-flex items-center gap-1">
                          <Link2 className="h-3 w-3" /> /{r.slug}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
